import appConfig from "@/appConfig";
import { AvatarDropdown, AvatarName, SelectLang } from "@/components";
import ChatUIKitProvider from "@/components/ChatUIKitProvider";
import {
  getAdminChatUser,
  getAllLocales,
  getContentManagerInit,
  queryPermissions,
  queryUser,
} from "@/services/ant-design-pro/api";
import { LocalStorage } from "@/utils/webStorage";
import { AliwangwangOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { SettingDrawer } from "@ant-design/pro-components";
import "@ant-design/v5-patch-for-react-19";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history } from "@umijs/max";
import { useState } from "react";
import { Link } from "umi";
import defaultSettings from "../config/defaultSettings";
import { errorConfig } from "./requestErrorConfig";
const isDev = process.env.NODE_ENV === "development";
const isDevOrTest = isDev || process.env.CI;
const loginPath = "/user/login";
/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const [user, permissions, allLocales, {data: schemaData}]: any = await Promise.all([
        queryUser(),
        queryPermissions(),
        getAllLocales(),
        getContentManagerInit(),
      ]);
      if (user?.errorCode === 401) LocalStorage.remove("jwtToken");

      // console.log(chatConfigData, "chatConfigData-res");
      return {
        name: `${user.data.firstname} ${user.data.lastname}`,
        ...user.data,
        permissions: permissions?.data,
        allLocales,
        schemaData,
      };
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, "/user/register", "/user/register-result"].includes(
      location.pathname
    )
  ) {
    let chatConfig: any = null;
    const currentUser = await fetchUserInfo();
    if (appConfig?.agoraChatAppKey && currentUser?.documentId) {
      const redData: any = await getAdminChatUser({});
      if (redData.code === 0)
        chatConfig = {
          appKey: appConfig?.agoraChatAppKey,
          ...redData.data,
        };
    }
    return {
      fetchUserInfo,
      currentUser,
      chatConfig,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const [open, setOpen] = useState(false);
  return {
    actionsRender: () => [
      // <Question key="doc" />,
      <AliwangwangOutlined onClick={() => setOpen(true)} />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_: any, avatarChildren: any) => (
        <AvatarDropdown>{avatarChildren}</AvatarDropdown>
      ),
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => null,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    // links: isDevOrTest
    //   ? [
    //       <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
    //         <LinkOutlined />
    //         <span>OpenAPI 文档</span>
    //       </Link>,
    //     ]
    //   : [],
    breadcrumbRender: false,
    menuHeaderRender: undefined,
    menuItemRender: (item: any, defaultDom: any) => {
      // 如果你想让某些菜单项（如groupMenu）有不同的样式但仍然可点击，可以这样实现：
      if (item.groupMenu) {
        return <div className={"group-menu"}>{defaultDom}</div>;
      }
      return item.external ? (
        <a
          href={appConfig.jumpToAdminUrl + (item.jumpPath || item.path)}
          target={item.target || "_blank"}
          rel="noopener noreferrer"
        >
          {defaultDom}
        </a>
      ) : (
        <Link to={item.path ?? "/"}>{defaultDom}</Link>
      );
      // 保持默认行为，确保菜单项可点击
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDevOrTest && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
          {initialState?.chatConfig?.token && (
            <ChatUIKitProvider
              open={open}
              onOpenChange={setOpen}
            ></ChatUIKitProvider>
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // baseURL: isDev ? "" : "https://proapi.azurewebsites.net",
  // baseURL: isDev ? "" : "https://top-up-admin-mock.shawnxavier888.workers.dev",
  baseURL: appConfig.adminBaseURL,
  ...errorConfig,
};
